/*
  High-level service for managing common alerting workflows, such as showing
  incident modals.
*/
import qs from 'query-string';
import { ngRoute } from '../../../app/routing/ngRoute';

const CROSS_APP_NAVIGATION_STORAGE_KEY = 'navigation-history-previous-page';
const DETECTORS_BREADCRUMB_TITLE = 'Detectors';
const INFRASTRUCTURE_BREADCRUMB_TITLE = 'Infrastructure';
const ALERT_SEARCH_PARAMS = ['eventId', 'incidentId'];

function buildReturnPath(pathname, searchParams, paramsToStrip) {
    const sanitizedSearchParams = { ...searchParams };
    paramsToStrip.forEach((param) => delete sanitizedSearchParams[param]);

    const search = qs.stringify(sanitizedSearchParams);

    return `${pathname}${search ? `?${search}` : ''}`;
}

class AlertingService {
    constructor(
        eventService,
        eventModal,
        alertTypeService,
        confirmService,
        featureEnabled,
        $location,
        $q,
        dashboardV2Service
    ) {
        this.eventService = eventService;
        this.eventModal = eventModal;
        this.alertTypeService = alertTypeService;
        this.confirmService = confirmService;
        this.featureEnabled = featureEnabled;
        this.$location = $location;
        this.$q = $q;
        this.dashboardV2Service = dashboardV2Service;
    }

    shouldOpenAlertDetailsPage() {
        return Boolean(this.featureEnabled('o11yAIITPageDefault'));
    }

    getDashboardNavigationUrl(pathname, searchParams = {}) {
        return buildReturnPath(pathname, searchParams, [...ALERT_SEARCH_PARAMS, 'viewTime']);
    }

    // Breadcrumb back to the current page for alert details; undefined for unsupported pages.
    getCurrentPageNavigationContext() {
        const currentPath = this.$location.path();
        const search = this.$location.search();

        if (currentPath.startsWith('/detector/')) {
            // viewTime is the detector chart's focused time, so keep it to restore the same view.
            return this.$q.resolve(
                this.createAlertDetailsNavigationContext({
                    dashboardName: DETECTORS_BREADCRUMB_TITLE,
                    dashboardUrl: buildReturnPath(currentPath, search, ALERT_SEARCH_PARAMS),
                })
            );
        }

        if (currentPath.startsWith('/navigator/')) {
            return this.$q.resolve(
                this.createAlertDetailsNavigationContext({
                    dashboardName: INFRASTRUCTURE_BREADCRUMB_TITLE,
                    dashboardUrl: buildReturnPath(currentPath, search, ALERT_SEARCH_PARAMS),
                })
            );
        }

        if (!currentPath.startsWith('/dashboard/') && !currentPath.startsWith('/page/')) {
            return this.$q.resolve(undefined);
        }

        const dashboardUrl = this.getDashboardNavigationUrl(currentPath, search);

        // /page/:groupId is a dashboard group id, not the individual dashboard
        // the chart belongs to, so it can't be resolved to a dashboard name here.
        const dashboardId = currentPath.startsWith('/dashboard/')
            ? currentPath.split('/')[2]
            : undefined;

        if (!dashboardId) {
            return this.$q.resolve(this.createAlertDetailsNavigationContext({ dashboardUrl }));
        }

        return this.dashboardV2Service.get(dashboardId).then(
            (dashboard) =>
                this.createAlertDetailsNavigationContext({
                    dashboardName: dashboard?.name || dashboard?.sf_dashboard,
                    dashboardUrl,
                }),
            () => this.createAlertDetailsNavigationContext({ dashboardUrl })
        );
    }

    createAlertDetailsNavigationContext({ dashboardName = 'Dashboard', dashboardUrl } = {}) {
        if (!dashboardUrl) {
            return undefined;
        }

        return {
            dashboardName,
            dashboardUrl,
        };
    }

    navigateToAlertDetails(eventId, navigationContext) {
        const pathname = `/dash/alert-details/${eventId}`;
        const dashboardPath = navigationContext?.dashboardUrl?.replace(/^#/, '');
        const dashboardName = navigationContext?.dashboardName;
        const search = qs.stringify({
            breadcrumbTitle: dashboardName,
            breadcrumbPath: dashboardPath,
        });
        const route = {
            pathname,
            search,
            state: dashboardPath
                ? {
                      breadcrumb: {
                          label: dashboardName,
                          pathname: dashboardPath,
                      },
                  }
                : undefined,
        };

        if (dashboardPath) {
            try {
                sessionStorage.setItem(
                    CROSS_APP_NAVIGATION_STORAGE_KEY,
                    JSON.stringify({
                        path: dashboardPath,
                        title: dashboardName || document.title || 'Dashboard',
                    })
                );
            } catch {
                // Ignore storage failures and continue with navigation.
            }
        }

        if (ngRoute.history) {
            ngRoute.history.push(route);
            return;
        }

        window.location.hash = `${pathname}${search ? `?${search}` : ''}`;
    }

    openIncidentModal(alert, navigationContext) {
        const incidentId = alert.incidentId || alert.sf_incidentId;
        const firingStateQuery = this.alertTypeService.getFiringStateQueryStr();

        return this.eventService
            .search({
                query: 'incidentId:"' + incidentId + '" AND is:(' + firingStateQuery + ')',
                orderBy: '-sf_timestamp',
                limit: 1,
                offset: 0,
            })
            .then((data) => {
                if (data && data.length) {
                    return data[0];
                } else {
                    // cant find event? should not happen in real life.
                    throw new Error('Event cannot be found');
                }
            })
            .then((event) => {
                if (this.shouldOpenAlertDetailsPage() && event?.id) {
                    this.navigateToAlertDetails(event.id, navigationContext);
                    return undefined;
                }

                return this.eventModal(event);
            })
            .catch((error) => {
                // show error message
                return this.confirmService.confirm({
                    title: 'Error',
                    text: [error.message],
                    yesText: 'Close',
                    noText: '',
                });
            });
    }
}

AlertingService.$inject = [
    'eventService',
    'eventModal',
    'alertTypeService',
    'confirmService',
    'featureEnabled',
    '$location',
    '$q',
    'dashboardV2Service',
];

export default AlertingService;
